<template>
  <Teleport to="body">
    <div class="pdf-viewer-capture-overlay" role="dialog" aria-modal="true">
      <!-- PDF Viewer wrapper — PdfViewer owns the toolbar; capture buttons live in its slot -->
      <div class="pdf-viewer-wrapper" :class="{ 'is-snipping': isSnipping }">
        <PdfViewer
          :pdf="pdfUrl"
          :accessible="accessible"
          :can-download="downloadable"
          :can-print="printable"
          @open="onPdfOpen"
          @close="handleClose"
        >
          <template #toolbar-open-in-new-tab>
            <template v-if="showOpenInNewTabButton">
              <button
                id="openInNewTab"
                class="toolbarButton open-in-new-tab"
                :title="t('pdf_open_in_new_tab_label')"
                @click="handleOpenInNewTab"
              >
                <span>{{ t("pdf_open_in_new_tab_label") }}</span>
              </button>
            </template>
          </template>
          <template #toolbar-capture>
            <!-- Capture controls — only shown when KB context is provided -->
            <template v-if="enableCapture">
              <!-- "CAPTURE:" label -->
              <span class="toolbarLabel capture-toolbar-slot-label">{{
                t("pdf_capture_label")
              }}</span>

              <!-- Snip tool button -->
              <button
                class="toolbarButton snip"
                :class="{ 'is-saving': savingAction === CaptureAction.SNIP }"
                type="button"
                :title="
                  isSnipping
                    ? t('pdf_capture_button_cancel_snip')
                    : t('pdf_capture_button_snip_tool')
                "
                :disabled="savingAction !== null || isRendering"
                @click="toggleSnipMode"
              >
                <span class="sr-only">{{
                  isSnipping
                    ? t("pdf_capture_button_cancel_snip")
                    : t("pdf_capture_button_snip_tool")
                }}</span>
              </button>

              <!-- Capture page button -->
              <button
                class="toolbarButton capture"
                :class="{ 'is-saving': savingAction === CaptureAction.PAGE }"
                type="button"
                :title="t('pdf_capture_button_capture_page')"
                :disabled="savingAction !== null || isRendering"
                @click="handleCapturePage"
              >
                <span class="sr-only">{{ t("pdf_capture_button_capture_page") }}</span>
              </button>

              <div class="verticalToolbarSeparator"></div>
            </template>
          </template>

          <template #toolbar-extra>
            <!-- Close button — disabled while a capture is saving to prevent mid-save close -->
            <button
              class="toolbarButton close"
              type="button"
              :title="t('button_close')"
              :disabled="isSaving"
              @click="handleClose"
            >
              <span class="sr-only">{{ t("button_close") }}</span>
            </button>
          </template>
        </PdfViewer>

        <!--
          Snip interaction layer — transparent div over the viewer,
          only mounted in snip mode to capture mouse drag coords.
        -->
        <div
          v-if="isSnipping"
          class="snip-interaction-layer"
          @mousedown.prevent="onSnipMouseDown"
          @touchstart.prevent="onSnipTouchStart"
        >
          <!-- Visual selection rectangle (uses fixed coords so it tracks the viewport) -->
          <div
            v-if="snipStart && snipEnd"
            class="snip-selection-rect"
            :style="snipSelectionStyle"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import PdfViewer from "@plugins/pdf-viewer/components/PdfViewer.vue";
import { usePdfViewerCapture } from "@modules/editor/composables/pdf-viewer-capture.ts";
import { PdfCaptureService } from "@modules/editor/service/pdf.capture.service.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import type { PDFViewer, EventBus } from "pdfjs-dist/web/pdf_viewer.mjs";
import type { RichTextContext } from "@plugins/richtext/types.ts";
import { useRouter } from "vue-router";
import mediaObjectApi from "@apis/media.object.api.ts";

// ─── Props & Emits ─────────────────────────────────────────────────────────────

interface Props {
  pdfUrl: string;
  pdfFilename: string;
  mediaObjectUuid: string;
  // KB capture context — omitted when opened in view-only mode
  resourceType?: string;
  resourceId?: number;
  currentKbCount?: number;
  maxKbAllowed?: number;
  richTextContext?: RichTextContext | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "image-saved", kbEntry: unknown): void;
  /**
   * Fires with the raw canvas data just before the save pipeline runs.
   * Useful for previews, analytics, or intercepting captures in dev tooling.
   */
  (e: "capture-ready", payload: { dataUrl: string; filename: string }): void;
}>();

// ─── Composables ───────────────────────────────────────────────────────────────

const { t } = useI18n();
const { show: showToast } = useToast();
const { passiveApiFailHandler } = useErrorHandler();
const router = useRouter();

const {
  currentPage,
  isRendering,
  isSnipping,
  snipStart,
  snipEnd,
  init: initCapture,
  getCurrentCanvas,
  captureCurrentPage,
  captureSnip,
  generateFilename,
} = usePdfViewerCapture();

// ─── Types ─────────────────────────────────────────────────────────────────────

const CaptureAction = {
  PAGE: "page",
  SNIP: "snip",
} as const;

type CaptureAction = (typeof CaptureAction)[keyof typeof CaptureAction];

// ─── Local state ───────────────────────────────────────────────────────────────

/** Which action is currently saving. Null when idle. */
const savingAction = ref<CaptureAction | null>(null);
const accessible = ref<boolean>(false);
const downloadable = ref<boolean>(false);
const printable = ref<boolean>(false);

/** True whenever any save is in flight — used to disable the close button. */
const isSaving = computed(() => savingAction.value !== null);

/** Capture mode is only enabled when full KB context is provided. */
const enableCapture = computed(
  () => !!props.resourceType && !!props.resourceId && typeof props.currentKbCount === "number",
);

/** Don't show open in new tab button if PDF viewed from LEA KB. KB is indicated by enable capture. **/
const showOpenInNewTabButton = computed(() => !enableCapture.value);

const captureService = computed(() =>
  enableCapture.value
    ? new PdfCaptureService({
        resourceType: props.resourceType!,
        resourceId: props.resourceId!,
        richTextContext: props.richTextContext ?? ({} as RichTextContext),
      })
    : null,
);

const getMediaObjectMetadata = async (uuid: string): Promise<void> => {
  try {
    const response = await mediaObjectApi.getMetaData({ uuid: uuid });
    if (response.code !== "ok") {
      passiveApiFailHandler(new Error("Media object metadata response not ok"));
    }
    const metaData = response.data;
    if (!metaData) {
      passiveApiFailHandler(new Error("Media object metadata does not exist"));
    }
    accessible.value = metaData?.accessible ?? false;
    downloadable.value = metaData?.downloadable ?? false;
    printable.value = metaData?.printable ?? false;
  } catch (e) {
    passiveApiFailHandler(e);
  }
};

// ─── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(() => {
  document.body.style.overflow = "hidden";
  getMediaObjectMetadata(props.mediaObjectUuid);
});

onBeforeUnmount(() => {
  document.body.style.overflow = "";
  document.removeEventListener("mousemove", onSnipMouseMove);
  document.removeEventListener("mouseup", onSnipMouseUpDocument);
  document.removeEventListener("touchmove", onSnipTouchMove);
  document.removeEventListener("touchend", onSnipTouchEndDocument);
});

// ─── PDF viewer event handlers ─────────────────────────────────────────────────

const onPdfOpen = ({
  pdfViewer,
  eventBus,
}: {
  pdfViewer: PDFViewer;
  eventBus: InstanceType<typeof EventBus>;
}) => {
  // Wire up capture composable with the real pdfViewer + eventBus instances
  initCapture({ pdfViewer, eventBus });
};

// ─── Max KB check ──────────────────────────────────────────────────────────────

const isMaxKbReached = computed(
  () => enableCapture.value && (props.currentKbCount ?? 0) >= (props.maxKbAllowed ?? Infinity),
);

// ─── Capture page ──────────────────────────────────────────────────────────────

const handleCapturePage = async () => {
  // Always exit snip mode when capture page is triggered — user may have
  // activated the snip tool but clicked capture page instead of drawing a region.
  resetSnip();

  if (isMaxKbReached.value) {
    showToast("error", {
      title: t("pdf_capture_toast_error_max_kb_title"),
      subTitle: t("pdf_capture_toast_error_max_kb_subtitle"),
    });
    return;
  }
  const dataUrl = captureCurrentPage();
  if (!dataUrl) return;
  const filename = generateFilename(props.pdfFilename, currentPage.value);
  await saveImageAsKb(dataUrl, filename, CaptureAction.PAGE);
};

const handleOpenInNewTab = () => {
  if (!props.mediaObjectUuid) {
    return;
  }
  const routeData = router.resolve({
    name: "PdfView",
    params: { uuid: props.mediaObjectUuid },
    query: { contextType: "", contextUuid: "" },
  });
  const newWindow = window.open(routeData.href, "_blank");
  if (newWindow) {
    handleClose();
  }
};

// ─── Snip tool ─────────────────────────────────────────────────────────────────

const toggleSnipMode = () => {
  isSnipping.value = !isSnipping.value;
  snipStart.value = null;
  snipEnd.value = null;
};

const onSnipMouseDown = (e: MouseEvent) => {
  snipStart.value = { x: e.clientX, y: e.clientY };
  snipEnd.value = null;
  document.addEventListener("mousemove", onSnipMouseMove);
  document.addEventListener("mouseup", onSnipMouseUpDocument, { once: true });
};

const onSnipMouseMove = (e: MouseEvent) => {
  if (!snipStart.value) return;
  snipEnd.value = { x: e.clientX, y: e.clientY };
};

const onSnipMouseUpDocument = () => {
  document.removeEventListener("mousemove", onSnipMouseMove);
  onSnipMouseUp();
};

// ─── Touch equivalents ─────────────────────────────────────────────────────────

const onSnipTouchStart = (e: TouchEvent) => {
  const touch = e.touches[0];
  snipStart.value = { x: touch.clientX, y: touch.clientY };
  snipEnd.value = null;
  document.addEventListener("touchmove", onSnipTouchMove, { passive: false });
  document.addEventListener("touchend", onSnipTouchEndDocument, { once: true });
};

const onSnipTouchMove = (e: TouchEvent) => {
  e.preventDefault();
  if (!snipStart.value) return;
  const touch = e.touches[0];
  snipEnd.value = { x: touch.clientX, y: touch.clientY };
};

const onSnipTouchEndDocument = () => {
  document.removeEventListener("touchmove", onSnipTouchMove);
  onSnipMouseUp();
};

const onSnipMouseUp = async () => {
  if (!snipStart.value || !snipEnd.value) {
    resetSnip();
    return;
  }

  // Width/height of the drag in screen pixels
  const screenW = Math.abs(snipStart.value.x - snipEnd.value.x);
  const screenH = Math.abs(snipStart.value.y - snipEnd.value.y);

  if (screenW < 5 || screenH < 5) {
    resetSnip();
    return;
  }

  if (isMaxKbReached.value) {
    showToast("error", {
      title: t("pdf_capture_toast_error_max_kb_title"),
      subTitle: t("pdf_capture_toast_error_max_kb_subtitle"),
    });
    resetSnip();
    return;
  }

  // Get the canvas element — getCurrentCanvas() uses the real pdfViewer instance
  const canvas = getCurrentCanvas();

  if (!canvas) {
    resetSnip();
    return;
  }

  const canvasRect = canvas.getBoundingClientRect();

  // Ratio of internal canvas pixels to CSS pixels (accounts for zoom & HiDPI)
  const scaleX = canvas.width / canvasRect.width;
  const scaleY = canvas.height / canvasRect.height;

  const x = (Math.min(snipStart.value.x, snipEnd.value.x) - canvasRect.left) * scaleX;
  const y = (Math.min(snipStart.value.y, snipEnd.value.y) - canvasRect.top) * scaleY;
  const w = screenW * scaleX;
  const h = screenH * scaleY;

  const dataUrl = captureSnip({ x, y, w, h });
  if (!dataUrl) {
    resetSnip();
    return;
  }

  const filename = generateFilename(props.pdfFilename, currentPage.value);
  resetSnip(); // clear the selection rect immediately before the async save
  await saveImageAsKb(dataUrl, filename, CaptureAction.SNIP);
};

const resetSnip = () => {
  snipStart.value = null;
  snipEnd.value = null;
  isSnipping.value = false;
};

/**
 * Snip selection rectangle style — uses fixed positioning so client coords map directly.
 */
const snipSelectionStyle = computed(() => {
  if (!snipStart.value || !snipEnd.value) return {};
  return {
    position: "fixed" as const,
    left: `${Math.min(snipStart.value.x, snipEnd.value.x)}px`,
    top: `${Math.min(snipStart.value.y, snipEnd.value.y)}px`,
    width: `${Math.abs(snipStart.value.x - snipEnd.value.x)}px`,
    height: `${Math.abs(snipStart.value.y - snipEnd.value.y)}px`,
  };
});

// ─── Save pipeline ─────────────────────────────────────────────────────────────

const saveImageAsKb = async (dataUrl: string, filename: string, action: CaptureAction) => {
  if (!captureService.value) return;
  emit("capture-ready", { dataUrl, filename });
  savingAction.value = action;
  try {
    const result = await captureService.value.captureAndSave({
      dataUrl,
      filename,
      indexNo: props.currentKbCount ?? 0,
    });
    showToast("success", { title: t("pdf_capture_toast_success_title") });
    emit("image-saved", result.kbEntry);
  } catch (err) {
    passiveApiFailHandler(err);
  } finally {
    savingAction.value = null;
  }
};

// ─── Close ─────────────────────────────────────────────────────────────────────

const handleClose = () => {
  emit("close");
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.pdf-viewer-capture-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  flex-direction: column;
  background: #404040;

  // ── PDF viewer wrapper ────────────────────────────────────────────────────────
  .pdf-viewer-wrapper {
    flex: 1;
    position: relative;
    overflow: hidden;
    min-height: 0;

    #pdf-viewer {
      height: 100%;
    }

    &.is-snipping {
      #viewerContainer,
      .page {
        cursor: crosshair !important;
      }
    }
  }

  .capture-toolbar-slot-label {
    padding: $spacing-02;
    white-space: nowrap;
    font-weight: bold;
  }

  // ── Snip interaction layer ────────────────────────────────────────────────────
  .snip-interaction-layer {
    position: absolute;
    inset: 0;
    z-index: 100;
    cursor: crosshair;
    background: transparent;
  }

  // ── Snip selection rectangle ──────────────────────────────────────────────────
  .snip-selection-rect {
    border: 2px solid $interactive-01;
    background: rgba(15, 98, 254, 0.08);
    pointer-events: none;
    z-index: 101;
  }
}
</style>
